import { useState, useCallback } from 'react';
import { apiService } from 'core/services/apiService';

/**
 * OAuth initiate response interface
 */
interface OAuthInitiateResponse {
	'@context'?: string;
	'@id'?: string;
	'@type'?: string;
	authUrl: string;
}

/**
 * Partner Attempt Parameters interface for OneDrive
 */
interface PartnerAttemptParameters {
	access_token?: string;
	encrypted_refresh_token?: string;
	token_expires_in?: number;
	token_expires_at?: string;
	scope?: string;
	token_type?: string;
	tenant?: string;
	connected_by_token?: string;
}

/**
 * Partner Attempt interface
 */
interface PartnerAttempt {
	'@id': string;
	'@type': string;
	id: number;
	parameter: PartnerAttemptParameters;
	user: string;
	workplace: string;
	type: string;
	driveId: string | null;
	createdAt: string;
}

/**
 * Partner Attempts API response (Hydra Collection)
 */
interface PartnerAttemptsResponse {
	'@context'?: string;
	'@id'?: string;
	'@type'?: string;
	totalItems?: number;
	'hydra:totalItems'?: number;
	member?: PartnerAttempt[];
	'hydra:member'?: PartnerAttempt[];
	/** Hydra PartialCollectionView — present when more pages exist */
	view?: {
		'@id'?: string;
		'@type'?: string;
		next?: string;
		previous?: string;
		first?: string;
		last?: string;
	};
}

/**
 * Get authentication data from localStorage
 */
const getAuthData = () => {
	try {
		const byodApp = localStorage.getItem('BYOD-app');
		const organizationId = localStorage.getItem('BYOD-organizationId');

		if (byodApp) {
			const data = JSON.parse(byodApp);
			return {
				token: data.token_key || '',
				tenant: organizationId || '',
				workplace: data.workplace || null,
				userId: data.user?.id || null,
			};
		}
	} catch (error) {
		console.error('Error reading auth data from localStorage:', error);
	}
	return { token: '', tenant: '', workplace: null, userId: null };
};

interface OneDriveAuthState {
	accessToken: string | null;
	refreshToken: string | null;
	isConnected: boolean;
	isLoading: boolean;
	error: string | null;
}

interface UseOneDriveAuthReturn extends OneDriveAuthState {
	initiateOneDriveAuth: () => void;
	resetAuth: () => void;
}

/**
 * Custom hook for Microsoft OneDrive OAuth 2.0
 * Mirrors the useGoogleAuth pattern — backend handles OAuth URL generation.
 * Polls partner_attempts API (type: 'one_drive') to detect a successful connection.
 */
export const useOneDriveAuth = (): UseOneDriveAuthReturn => {
	const [authState, setAuthState] = useState<OneDriveAuthState>({
		accessToken: null,
		refreshToken: null,
		isConnected: false,
		isLoading: false,
		error: null,
	});

	/**
	 * Poll partner_attempts API to check for a successful OneDrive connection.
	 * Paginates through all records so attempts beyond the first page are not missed.
	 */
	const pollPartnerAttempts = useCallback(
		async (
			userId: number,
			workplaceId: number,
			startTime: Date
		): Promise<PartnerAttempt | null> => {
			let page = 1;

			try {
				let morePages = true;
				while (morePages) {
					const response = await apiService.get<PartnerAttemptsResponse>(`/partner_attempts`, {
						params: {
							page,
							itemsPerPage: 30,
							'user.id': userId,
							'workplace.id': workplaceId,
							type: 'one_drive',
						},
					});

					const attempts = response.data.member || response.data['hydra:member'] || [];
					const totalItems = response.data.totalItems ?? response.data['hydra:totalItems'] ?? 0;

					console.log(`[OneDrive OAuth] Page ${page}: fetched ${attempts.length} of ${totalItems} total attempts`);

					// Check this page for a matching attempt
					const recentAttempt = attempts.find((attempt) => {
						const attemptTime = new Date(attempt.createdAt);
						const hasToken = !!attempt.parameter?.access_token;
						const isAfterStart = attemptTime >= startTime;

						console.log('[OneDrive OAuth] Checking attempt:', {
							id: attempt.id,
							createdAt: attempt.createdAt,
							hasToken,
							isAfterStart,
							matches: isAfterStart && hasToken,
						});

						return isAfterStart && hasToken;
					});

					if (recentAttempt) {
						console.log('[OneDrive OAuth] Found matching attempt on page', page, '— ID', recentAttempt.id);
						return recentAttempt;
					}

					// Follow Hydra view.next link if present, otherwise we're on the last page
					if (response.data.view?.next) {
						page++;
					} else {
						console.log('[OneDrive OAuth] No view.next — all pages exhausted, no matching attempt found');
						morePages = false;
					}
				}

				return null;
			} catch (error) {
				console.error('[OneDrive OAuth] Error polling partner attempts:', error);
				return null;
			}
		},
		[]
	);

	/**
	 * Initiates Microsoft OneDrive OAuth flow.
	 * Calls backend to get the OAuth URL, opens it in a popup window,
	 * then polls partner_attempts until tokens are available or timeout occurs.
	 */
	const initiateOneDriveAuth = useCallback(async () => {
		try {
			setAuthState((prev) => ({
				...prev,
				isLoading: true,
				error: null,
			}));

			const { token, userId, workplace } = getAuthData();

			if (!token || !userId) {
				throw new Error('User not authenticated. Please log in again.');
			}

			const workplaceId = workplace
				? typeof workplace === 'object'
					? workplace.id
					: workplace
				: null;

			if (!workplaceId) {
				throw new Error('No workplace selected. Please select a workplace.');
			}

			console.log(
				'[OneDrive OAuth] Starting OAuth flow for user:',
				userId,
				'workplace:',
				workplaceId
			);

			// Record the start time to filter only new attempts
			const startTime = new Date();

			// Call backend API to get the Microsoft OAuth URL
			const response = await apiService.post<OAuthInitiateResponse>(
				'/oauth/ms-onedrive/initiate',
				{} // Empty body — backend derives context from the authenticated session
			);

			const { authUrl } = response.data;

			if (!authUrl) {
				throw new Error('Failed to get OAuth URL from backend');
			}

			console.log('[OneDrive OAuth] Opening OAuth window with URL:', authUrl);

			// Open the Microsoft OAuth consent screen in a popup
			const authWindow = window.open(
				authUrl,
				'onedrive-oauth',
				'width=500,height=600,scrollbars=yes'
			);

			if (!authWindow) {
				throw new Error(
					'Failed to open OAuth window. Please allow popups for this site.'
				);
			}

			console.log('[OneDrive OAuth] OAuth window opened, starting to poll for partner attempts');

			// Poll for up to 5 minutes (60 × 5 s = 300 s)
			let pollCount = 0;
			const maxPolls = 60;
			const pollInterval = 5000;

			const doPoll = async (): Promise<'stop' | 'continue'> => {
				pollCount++;
				console.log(
					`[OneDrive OAuth] Polling attempt ${pollCount}/${maxPolls} for user ${userId}, workplace ${workplaceId}`
				);

				const attempt = await pollPartnerAttempts(userId, workplaceId, startTime);

				if (attempt && attempt.parameter?.access_token) {
					console.log('[OneDrive OAuth] Success! Partner attempt found:', attempt);

					// Close the auth window. Retry a few times because cross-origin
					// navigation can make the first close() call race with the page load.
					const closeAuthWindow = () => {
						try {
							if (authWindow && !authWindow.closed) {
								authWindow.close();
							}
						} catch (e) {
							console.log('[OneDrive OAuth] Could not close auth window:', e);
						}
					};
					console.log('[OneDrive OAuth] Closing auth window');
					closeAuthWindow();
					setTimeout(closeAuthWindow, 500);
					setTimeout(closeAuthWindow, 1500);

					setAuthState({
						accessToken: attempt.parameter.access_token,
						refreshToken: attempt.parameter.encrypted_refresh_token || null,
						isConnected: true,
						isLoading: false,
						error: null,
					});
					return 'stop';
				}

				if (pollCount >= maxPolls) {
					console.log('[OneDrive OAuth] Polling timeout reached after', pollCount, 'attempts');

					try {
						if (authWindow && !authWindow.closed) {
							authWindow.close();
						}
					} catch (e) {
						console.log('[OneDrive OAuth] Could not close auth window on timeout:', e);
					}

					setAuthState((prev) => ({
						...prev,
						isLoading: false,
						error: 'Authentication timed out. Please try again.',
					}));
					return 'stop';
				}

				return 'continue';
			};

			// Start first poll after a brief delay to give the window time to load
			setTimeout(async () => {
				console.log('[OneDrive OAuth] Starting polling...');
				const result = await doPoll();

				if (result === 'stop') return;

				// Continue polling on the interval
				const intervalId = setInterval(async () => {
					const result = await doPoll();
					if (result === 'stop') {
						clearInterval(intervalId);
					}
				}, pollInterval);
			}, 500);
		} catch (error: any) {
			console.error('[OneDrive OAuth] Error initiating OAuth:', error);
			setAuthState({
				accessToken: null,
				refreshToken: null,
				isConnected: false,
				isLoading: false,
				error: error.message || 'Failed to initiate Microsoft OneDrive authentication',
			});
		}
	}, [pollPartnerAttempts]);

	/**
	 * Resets OneDrive authentication state
	 */
	const resetAuth = useCallback(() => {
		setAuthState({
			accessToken: null,
			refreshToken: null,
			isConnected: false,
			isLoading: false,
			error: null,
		});
	}, []);

	return {
		...authState,
		initiateOneDriveAuth,
		resetAuth,
	};
};
